import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, Truck, Package, Pencil, Check, X, ChevronDown, ChevronUp, AlertCircle, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';
import { fmtDate } from '@/lib/utils';

const STATUS_CONFIG = {
  overdue: { label: 'Overdue', className: 'bg-red-50 text-red-700 border-red-200' },
  today: { label: 'Today', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  upcoming: { label: 'Soon', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  scheduled: { label: 'Scheduled', className: 'bg-zinc-100 text-zinc-600 border-zinc-200' }
};

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };
const FREQ_LABELS = { weekly: 'Every Week', biweekly: 'Every 2 Weeks', monthly: 'Once a Month' };

function SupplierCard({ supplier, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [freq, setFreq] = useState(supplier.frequency || 'weekly');
  const [visitDay, setVisitDay] = useState(supplier.visit_day || 'monday');
  const [lastVisit, setLastVisit] = useState(supplier.last_visit_date || '');
  const [notes, setNotes] = useState(supplier.notes || '');
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);

  const statusCfg = STATUS_CONFIG[supplier.status] || STATUS_CONFIG.scheduled;

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/suppliers/schedule', {
        supplier: supplier.supplier,
        frequency: freq,
        visit_day: visitDay,
        last_visit_date: lastVisit || null,
        notes
      });
      toast.success(`Schedule updated for ${supplier.supplier}`);
      setEditing(false);
      onUpdate?.();
    } catch {
      toast.error('Failed to update schedule');
    } finally { setSaving(false); }
  };

  const handleCancel = () => {
    setEditing(false);
    setFreq(supplier.frequency || 'weekly');
    setVisitDay(supplier.visit_day || 'monday');
    setLastVisit(supplier.last_visit_date || '');
    setNotes(supplier.notes || '');
  };

  return (
    <div
      data-testid={`supplier-card-${supplier.supplier.toLowerCase().replace(/\s+/g, '-')}`}
      className={`border bg-white transition-colors ${supplier.is_configured ? 'border-zinc-200 hover:border-zinc-300' : 'border-dashed border-zinc-200 bg-zinc-50/30'}`}
    >
      {/* Header */}
      <div className="p-3 sm:p-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`h-8 w-8 flex items-center justify-center shrink-0 ${supplier.is_configured ? 'bg-emerald-50' : 'bg-zinc-100'}`}>
            <Truck className={`h-4 w-4 ${supplier.is_configured ? 'text-emerald-600' : 'text-zinc-400'}`} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-sm font-bold text-zinc-900 truncate" style={{ fontFamily: "'Chivo', sans-serif" }}>
                {supplier.supplier}
              </h4>
              {supplier.is_configured && (
                <Badge className={`${statusCfg.className} text-[10px] font-bold tracking-wider uppercase rounded-sm px-1.5 py-0`}>
                  {statusCfg.label}
                </Badge>
              )}
              {!supplier.is_configured && (
                <span className="text-[10px] text-zinc-400 italic">Not scheduled</span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-0.5 text-xs text-zinc-500">
              <span><span className="font-mono font-semibold text-zinc-700">{supplier.total_products}</span> products</span>
              {supplier.is_configured && (
                <>
                  <span className="text-zinc-300">|</span>
                  <span className="capitalize">{FREQ_LABELS[supplier.frequency] || supplier.frequency} on <span className="font-semibold text-zinc-700 capitalize">{supplier.visit_day}</span></span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Link
            to={`/supplier-view?supplier=${encodeURIComponent(supplier.supplier)}`}
            data-testid={`view-stock-${supplier.supplier.toLowerCase().replace(/\s+/g, '-')}`}
            className="flex items-center gap-1.5 h-7 px-2.5 text-xs font-medium border border-zinc-300 text-zinc-600 hover:border-emerald-500 hover:text-emerald-700 hover:bg-emerald-50/50 transition-colors"
            title="Open stock view for this supplier"
          >
            <BarChart3 className="h-3 w-3" />
            <span className="hidden sm:inline">Stock</span>
          </Link>
          {supplier.is_configured && (
            <div className="text-right mr-1 hidden sm:block">
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Next Visit</p>
              <p className={`text-sm font-mono font-semibold ${
                supplier.status === 'overdue' ? 'text-red-600' :
                supplier.status === 'today' ? 'text-emerald-600' : 'text-zinc-700'
              }`}>
                {supplier.next_visit}
              </p>
              <p className="text-[10px] text-zinc-400">
                {supplier.days_until_visit < 0 ? `${Math.abs(supplier.days_until_visit)}d overdue` :
                 supplier.days_until_visit === 0 ? 'Today' :
                 `in ${supplier.days_until_visit}d`}
              </p>
            </div>
          )}
          <Button variant="ghost" size="icon" className="h-7 w-7 rounded-none" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>

      {/* Expanded */}
      {expanded && (
        <div className="border-t border-zinc-100 p-3 sm:p-4 bg-zinc-50/50 animate-fade-in space-y-4">
          {/* Schedule controls */}
          {editing ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="field-label">Frequency</label>
                  <Select value={freq} onValueChange={setFreq}>
                    <SelectTrigger data-testid={`freq-select-${supplier.supplier.toLowerCase().replace(/\s+/g, '-')}`} className="rounded-none border-zinc-300 h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Every Week</SelectItem>
                      <SelectItem value="biweekly">Every 2 Weeks</SelectItem>
                      <SelectItem value="monthly">Once a Month</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="field-label">Visit Day</label>
                  <Select value={visitDay} onValueChange={setVisitDay}>
                    <SelectTrigger data-testid={`day-select-${supplier.supplier.toLowerCase().replace(/\s+/g, '-')}`} className="rounded-none border-zinc-300 h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS.map(d => (
                        <SelectItem key={d} value={d} className="capitalize">{d.charAt(0).toUpperCase() + d.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="field-label">Last Visited (optional)</label>
                  <Input
                    data-testid={`last-visit-${supplier.supplier.toLowerCase().replace(/\s+/g, '-')}`}
                    type="date"
                    value={lastVisit}
                    onChange={(e) => setLastVisit(e.target.value)}
                    className="rounded-none border-zinc-300 h-8 text-sm font-mono focus:border-emerald-600 focus-visible:ring-0"
                  />
                </div>
              </div>
              <div>
                <label className="field-label">Notes</label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g., Call before 10 AM, delivery boy name, etc."
                  className="rounded-none border-zinc-300 h-8 text-sm focus:border-emerald-600 focus-visible:ring-0"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  data-testid={`save-schedule-${supplier.supplier.toLowerCase().replace(/\s+/g, '-')}`}
                  size="sm" onClick={handleSave} disabled={saving}
                  className="h-8 rounded-none bg-emerald-600 hover:bg-emerald-700 text-white px-4"
                >
                  <Check className="h-3.5 w-3.5 mr-1" /> {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button size="sm" variant="ghost" onClick={handleCancel} className="h-8 rounded-none px-3">
                  <X className="h-3.5 w-3.5 mr-1" /> Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-6 text-xs text-zinc-500">
                <div>
                  <span className="field-label block">Frequency</span>
                  <span className="text-sm text-zinc-800 font-medium capitalize">{FREQ_LABELS[supplier.frequency] || 'Weekly'}</span>
                </div>
                <div>
                  <span className="field-label block">Day</span>
                  <span className="text-sm text-zinc-800 font-medium capitalize">{supplier.visit_day || 'Not set'}</span>
                </div>
                {supplier.last_visit_date && (
                  <div>
                    <span className="field-label block">Last Visited</span>
                    <span className="text-sm text-zinc-800 font-mono">{fmtDate(supplier.last_visit_date)}</span>
                  </div>
                )}
                {supplier.notes && (
                  <div>
                    <span className="field-label block">Notes</span>
                    <span className="text-sm text-zinc-600 italic">{supplier.notes}</span>
                  </div>
                )}
              </div>
              <Button
                data-testid={`edit-schedule-${supplier.supplier.toLowerCase().replace(/\s+/g, '-')}`}
                size="sm" variant="outline" onClick={() => setEditing(true)}
                className="h-8 rounded-none border-zinc-300 px-3"
              >
                <Pencil className="h-3 w-3 mr-1.5" /> {supplier.is_configured ? 'Edit' : 'Set Schedule'}
              </Button>
            </div>
          )}

          {/* Top products */}
          {supplier.top_products?.length > 0 && (
            <div>
              <p className="field-label mb-2">Top Products to Order</p>
              <div className="space-y-1">
                {supplier.top_products.map((p) => (
                  <div key={p.name} className="flex items-center justify-between text-xs py-1.5 px-2 bg-white border border-zinc-100">
                    <span className="text-zinc-700 font-medium">{p.name}</span>
                    <div className="flex items-center gap-4">
                      <span className="text-zinc-400">Sold: <span className="font-mono font-semibold text-zinc-600">{p.total_sold}</span></span>
                      <span className="text-emerald-600 font-mono font-bold">Order: {p.suggested}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SupplierVisits() {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);

  const fetchVisits = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/suppliers/visits');
      setVisits(data);
    } catch (e) { console.error('Failed to fetch supplier schedules:', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchVisits(); }, [fetchVisits]);

  const configured = visits.filter(v => v.is_configured);
  const notConfigured = visits.filter(v => !v.is_configured);
  const overdueCount = configured.filter(v => v.status === 'overdue').length;
  const todayCount = configured.filter(v => v.status === 'today').length;

  return (
    <div>
      <button
        data-testid="supplier-visits-toggle"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 mb-3 text-sm font-bold text-zinc-900 hover:text-emerald-700 transition-colors"
        style={{ fontFamily: "'Chivo', sans-serif" }}
      >
        <CalendarClock className="h-4 w-4 text-emerald-600" />
        Supplier Visit Schedule
        {overdueCount > 0 && (
          <span className="bg-red-100 text-red-700 border border-red-200 text-[10px] font-bold px-1.5 py-0 rounded-sm uppercase">
            {overdueCount} overdue
          </span>
        )}
        {todayCount > 0 && (
          <span className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-[10px] font-bold px-1.5 py-0 rounded-sm uppercase">
            {todayCount} today
          </span>
        )}
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>

      {open && (
        <div data-testid="supplier-visits-section" className="space-y-2 animate-fade-in">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-6 w-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : visits.length === 0 ? (
            <div className="border border-zinc-200 bg-zinc-50 p-6 text-center">
              <Package className="h-8 w-8 text-zinc-300 mx-auto mb-2" />
              <p className="text-sm text-zinc-500">No suppliers found. Upload sales data to see suppliers here.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-4 px-1 mb-1 text-xs text-zinc-500">
                <span><span className="font-mono font-semibold text-zinc-700">{configured.length}</span> scheduled</span>
                <span><span className="font-mono font-semibold text-zinc-700">{notConfigured.length}</span> not scheduled</span>
                {overdueCount > 0 && (
                  <span className="flex items-center gap-1 text-red-600">
                    <AlertCircle className="h-3 w-3" /> {overdueCount} overdue
                  </span>
                )}
              </div>

              {/* Configured suppliers first */}
              {configured.map((v) => (
                <SupplierCard key={v.supplier} supplier={v} onUpdate={fetchVisits} />
              ))}

              {/* Not configured */}
              {notConfigured.length > 0 && (
                <details className="group">
                  <summary className="cursor-pointer text-xs text-zinc-400 hover:text-zinc-600 py-2 select-none">
                    {notConfigured.length} suppliers without schedule — click to expand
                  </summary>
                  <div className="space-y-2 mt-1">
                    {notConfigured.map((v) => (
                      <SupplierCard key={v.supplier} supplier={v} onUpdate={fetchVisits} />
                    ))}
                  </div>
                </details>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
